import { monthKey } from './date.js'
import { isWeekAchieved, type WeekSummary } from './goals.js'

/**
 * Le streak : des semaines consécutives où l'objectif personnel est atteint.
 *
 * Deux règles de bienveillance, qui sont un choix produit assumé :
 *  1. La semaine EN COURS ne casse jamais le streak — elle n'est pas finie.
 *  2. Un JOKER par mois calendaire : la première semaine ratée d'un mois ne
 *     casse pas la série (elle ne l'incrémente pas non plus). MovMania
 *     récompense la constance, pas la perfection.
 */
export interface StreakResult {
  /** Nombre de semaines validées dans la série en cours */
  current: number
  /** Meilleure série jamais atteinte sur la période analysée */
  best: number
  /** Mois calendaires où un joker a été consommé */
  jokersUsed: string[]
  /** L'objectif de la semaine en cours est-il déjà atteint ? */
  currentWeekAchieved: boolean
  /** Un joker est-il encore disponible ce mois-ci ? */
  jokerAvailableThisMonth: boolean
}

export function computeStreak(
  summaries: readonly WeekSummary[],
  weeklyGoalMin: number,
): StreakResult {
  const sorted = [...summaries].sort((a, b) =>
    a.weekStart < b.weekStart ? -1 : a.weekStart > b.weekStart ? 1 : 0,
  )

  const jokerMonths = new Set<string>()
  let run = 0
  let best = 0
  let current = 0

  for (const summary of sorted) {
    if (isWeekAchieved(summary, weeklyGoalMin)) {
      run += 1
      best = Math.max(best, run)
    } else if (summary.isCurrent) {
      // Semaine en cours non encore validée : on ne tranche pas, on attend.
    } else {
      const month = monthKey(new Date(summary.weekStart))
      if (jokerMonths.has(month)) {
        run = 0 // joker déjà utilisé ce mois-ci : la série casse
      } else {
        jokerMonths.add(month) // joker consommé, la série survit
      }
    }
    current = run
  }

  const last = sorted[sorted.length - 1]
  const currentWeekAchieved = last
    ? last.isCurrent && isWeekAchieved(last, weeklyGoalMin)
    : false
  const thisMonth = last ? monthKey(new Date(last.weekStart)) : ''

  return {
    current,
    best,
    jokersUsed: [...jokerMonths],
    currentWeekAchieved,
    jokerAvailableThisMonth: !jokerMonths.has(thisMonth),
  }
}
